import { db } from '../firebase';
import { collection, addDoc, getDocs, Timestamp } from 'firebase/firestore';

/**
 * Seed initial email templates to Firestore
 * Run this once to populate the database with default templates
 */

const defaultTemplates = [
	{
		name: 'Portal Invitation',
		category: 'portal',
		subject: 'Welcome to Color Your Voice Student Portal - {{studentName}}',
		description: 'Welcome email sent when inviting parents to access the student portal',
		htmlContent: `<!DOCTYPE html>
<html>
<head>
  <style>
    body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
    .container { max-width: 600px; margin: 0 auto; padding: 20px; }
    .header { background: linear-gradient(135deg, #384971 0%, #82c6c6 100%); color: white; padding: 30px; text-align: center; border-radius: 8px 8px 0 0; }
    .content { background: #f9f9f9; padding: 30px; border-radius: 0 0 8px 8px; }
    .button { display: inline-block; background: #384971; color: white; padding: 12px 30px; text-decoration: none; border-radius: 5px; margin: 20px 0; }
    .credentials { background: white; padding: 15px; border-left: 4px solid #82c6c6; margin: 20px 0; }
    .footer { text-align: center; margin-top: 30px; color: #666; font-size: 12px; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>🎵 Welcome to Color Your Voice!</h1>
    </div>
    <div class="content">
      <p>Dear {{parentName}},</p>

      <p>We're excited to welcome <strong>{{studentName}}</strong> to our student portal! This portal gives you 24/7 access to:</p>

      <ul>
        <li>Lesson schedules and history</li>
        <li>Practice logs and assignments</li>
        <li>Payment information</li>
        <li>Direct communication with instructors</li>
      </ul>

      <div class="credentials">
        <strong>Your Login Information:</strong><br>
        Email: {{parentEmail}}<br>
        Temporary Password: <code>{{temporaryPassword}}</code><br>
        <small>Please change this password after your first login.</small>
      </div>

      <a href="{{loginUrl}}" class="button">Access Student Portal →</a>

      <p>If you have any questions, please don't hesitate to reach out!</p>

      <p>Best regards,<br>
      Color Your Voice</p>
    </div>
    <div class="footer">
      <p>© ${new Date().getFullYear()} Color Your Voice. All rights reserved.</p>
    </div>
  </div>
</body>
</html>`,
		variables: ['studentName', 'parentName', 'parentEmail', 'loginUrl', 'temporaryPassword']
	},
	{
		name: 'Lesson Notes',
		category: 'lesson',
		subject: 'Lesson Summary for {{studentName}} - {{lessonDate}}',
		description: 'Summary email sent to parents after each lesson',
		htmlContent: `<!DOCTYPE html>
<html>
<head>
  <style>
    body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
    .container { max-width: 600px; margin: 0 auto; padding: 20px; }
    .header { background: linear-gradient(135deg, #384971 0%, #82c6c6 100%); color: white; padding: 30px; text-align: center; border-radius: 8px 8px 0 0; }
    .content { background: #f9f9f9; padding: 30px; border-radius: 0 0 8px 8px; }
    .notes-box { background: white; padding: 20px; border-left: 4px solid #82c6c6; margin: 20px 0; }
    .next-lesson { background: #ffe7cc; padding: 15px; border-radius: 5px; margin: 20px 0; }
    .footer { text-align: center; margin-top: 30px; color: #666; font-size: 12px; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>🎵 Lesson Summary</h1>
    </div>
    <div class="content">
      <p>Dear {{parentName}},</p>

      <p>Here's a summary of <strong>{{studentName}}'s</strong> lesson on <strong>{{lessonDate}}</strong> at <strong>{{lessonTime}}</strong>:</p>

      <div class="notes-box">
        <strong>Lesson Notes:</strong><br>
        {{lessonNotes}}
      </div>

      <div class="next-lesson">
        <strong>📅 Next Lesson:</strong> {{nextLessonDate}}
      </div>

      <p>Keep up the great work! If you have any questions about today's lesson, feel free to reach out.</p>

      <p>Best regards,<br>
      Color Your Voice</p>
    </div>
    <div class="footer">
      <p>© ${new Date().getFullYear()} Color Your Voice. All rights reserved.</p>
    </div>
  </div>
</body>
</html>`,
		variables: ['studentName', 'parentName', 'parentEmail', 'lessonDate', 'lessonTime', 'lessonNotes', 'nextLessonDate']
	},
	{
		name: 'Lesson Reminder',
		category: 'reminder',
		subject: 'Reminder: {{studentName}} has a lesson tomorrow!',
		description: 'Automated reminder sent the day before a scheduled lesson',
		htmlContent: `<!DOCTYPE html>
<html>
<head>
  <style>
    body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
    .container { max-width: 600px; margin: 0 auto; padding: 20px; }
    .header { background: linear-gradient(135deg, #384971 0%, #82c6c6 100%); color: white; padding: 30px; text-align: center; border-radius: 8px 8px 0 0; }
    .content { background: #f9f9f9; padding: 30px; border-radius: 0 0 8px 8px; }
    .reminder-box { background: #fff3cd; padding: 20px; border-left: 4px solid #ffb953; margin: 20px 0; text-align: center; }
    .footer { text-align: center; margin-top: 30px; color: #666; font-size: 12px; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>🔔 Lesson Reminder</h1>
    </div>
    <div class="content">
      <p>Dear {{parentName}},</p>

      <div class="reminder-box">
        <h2 style="margin-top: 0; color: #856404;">Tomorrow's Lesson</h2>
        <p style="font-size: 18px; margin: 10px 0;">
          <strong>{{studentName}}</strong><br>
          {{lessonDate}} at {{lessonTime}}
        </p>
      </div>

      <p>This is a friendly reminder about tomorrow's lesson. We look forward to seeing you!</p>

      <p>Best regards,<br>
      Color Your Voice</p>
    </div>
    <div class="footer">
      <p>© ${new Date().getFullYear()} Color Your Voice. All rights reserved.</p>
    </div>
  </div>
</body>
</html>`,
		variables: ['studentName', 'parentName', 'parentEmail', 'lessonDate', 'lessonTime']
	},
	{
		name: 'Payment Receipt',
		category: 'payment',
		subject: 'Payment Receipt - Invoice #{{invoiceNumber}}',
		description: 'Receipt sent after a payment is received',
		htmlContent: `<!DOCTYPE html>
<html>
<head>
  <style>
    body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
    .container { max-width: 600px; margin: 0 auto; padding: 20px; }
    .header { background: linear-gradient(135deg, #384971 0%, #82c6c6 100%); color: white; padding: 30px; text-align: center; border-radius: 8px 8px 0 0; }
    .content { background: #f9f9f9; padding: 30px; border-radius: 0 0 8px 8px; }
    .receipt-box { background: white; padding: 20px; border: 2px solid #82c6c6; margin: 20px 0; }
    .total { background: #e7f6f6; padding: 10px; font-size: 18px; font-weight: bold; text-align: right; }
    .footer { text-align: center; margin-top: 30px; color: #666; font-size: 12px; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>💳 Payment Receipt</h1>
    </div>
    <div class="content">
      <p>Dear {{parentName}},</p>

      <p>Thank you for your payment! Here's your receipt:</p>

      <div class="receipt-box">
        <p><strong>Invoice Number:</strong> {{invoiceNumber}}</p>
        <p><strong>Date:</strong> {{paymentDate}}</p>
        <p><strong>Payment Method:</strong> {{paymentMethod}}</p>
        <p><strong>Student:</strong> {{studentName}}</p>
        <hr>
        <div class="total">
          Amount Paid: ${'{{amount}}'}
        </div>
        <p style="margin-top: 10px;"><strong>Remaining Balance:</strong> ${'{{balance}}'}</p>
      </div>

      <p>We appreciate your continued support. If you have any questions about this payment, please don't hesitate to contact us.</p>

      <p>Best regards,<br>
      Color Your Voice</p>
    </div>
    <div class="footer">
      <p>© ${new Date().getFullYear()} Color Your Voice. All rights reserved.</p>
    </div>
  </div>
</body>
</html>`,
		variables: ['studentName', 'parentName', 'parentEmail', 'amount', 'paymentDate', 'paymentMethod', 'invoiceNumber', 'balance']
	},
	{
		name: 'General Announcement',
		category: 'announcement',
		subject: '{{title}}',
		description: 'General announcement template for mass emails',
		htmlContent: `<!DOCTYPE html>
<html>
<head>
  <style>
    body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
    .container { max-width: 600px; margin: 0 auto; padding: 20px; }
    .header { background: linear-gradient(135deg, #384971 0%, #82c6c6 100%); color: white; padding: 30px; text-align: center; border-radius: 8px 8px 0 0; }
    .content { background: #f9f9f9; padding: 30px; border-radius: 0 0 8px 8px; }
    .message-box { background: white; padding: 20px; border-left: 4px solid #f933a4; margin: 20px 0; }
    .footer { text-align: center; margin-top: 30px; color: #666; font-size: 12px; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>📢 {{title}}</h1>
    </div>
    <div class="content">
      <p>Dear {{parentName}},</p>

      <div class="message-box">
        {{message}}
      </div>

      <p>If you have any questions, please feel free to reach out to us.</p>

      <p>Best regards,<br>
      Color Your Voice</p>
    </div>
    <div class="footer">
      <p>© ${new Date().getFullYear()} Color Your Voice. All rights reserved.</p>
    </div>
  </div>
</body>
</html>`,
		variables: ['parentName', 'title', 'message']
	},
	{
		name: 'Password Reset',
		category: 'portal',
		subject: 'Reset Your Password - Color Your Voice Portal',
		description: 'Password reset email with temporary credentials',
		htmlContent: `<!DOCTYPE html>
<html>
<head>
  <style>
    body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
    .container { max-width: 600px; margin: 0 auto; padding: 20px; }
    .header { background: linear-gradient(135deg, #384971 0%, #82c6c6 100%); color: white; padding: 30px; text-align: center; border-radius: 8px 8px 0 0; }
    .content { background: #f9f9f9; padding: 30px; border-radius: 0 0 8px 8px; }
    .button { display: inline-block; background: #384971; color: white; padding: 12px 30px; text-decoration: none; border-radius: 5px; margin: 20px 0; }
    .credentials { background: white; padding: 15px; border-left: 4px solid #ed7749; margin: 20px 0; }
    .footer { text-align: center; margin-top: 30px; color: #666; font-size: 12px; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>🔐 Password Reset</h1>
    </div>
    <div class="content">
      <p>Dear {{parentName}},</p>

      <p>We received a request to reset your password for the Color Your Voice Student Portal.</p>

      <div class="credentials">
        <strong>Your New Temporary Password:</strong><br>
        <code>{{temporaryPassword}}</code><br>
        <small>Please change this password immediately after logging in.</small>
      </div>

      <a href="{{loginUrl}}" class="button">Login to Portal →</a>

      <p><strong>Security Note:</strong> If you did not request this password reset, please contact us immediately.</p>

      <p>Best regards,<br>
      Color Your Voice</p>
    </div>
    <div class="footer">
      <p>© ${new Date().getFullYear()} Color Your Voice. All rights reserved.</p>
    </div>
  </div>
</body>
</html>`,
		variables: ['parentName', 'parentEmail', 'loginUrl', 'temporaryPassword']
	}
];

export const seedEmailTemplates = async () => {
	try {
		console.log('Starting email template seeding...');

		// Check if templates already exist
		const existingTemplates = await getDocs(collection(db, 'emailTemplates'));

		if (existingTemplates.docs.length > 0) {
			console.log(`Found ${existingTemplates.docs.length} existing templates. Skipping seed.`);
			return {
				success: true,
				message: 'Templates already exist. No seeding needed.',
				count: existingTemplates.docs.length
			};
		}

		// Add all default templates
		const results = [];
		for (const template of defaultTemplates) {
			const docRef = await addDoc(collection(db, 'emailTemplates'), {
				...template,
				createdAt: Timestamp.now(),
				updatedAt: Timestamp.now()
			});
			results.push({ id: docRef.id, name: template.name });
			console.log(`✓ Created template: ${template.name}`);
		}

		console.log(`Successfully seeded ${results.length} email templates!`);

		return {
			success: true,
			message: `Successfully created ${results.length} email templates`,
			templates: results
		};
	} catch (error) {
		console.error('Error seeding email templates:', error);
		return {
			success: false,
			error: error.message
		};
	}
};

export default seedEmailTemplates;
